import type { FunctionRegistryEntry, FunctionCategory } from './types';

export const FUNCTION_REGISTRY: FunctionRegistryEntry[] = [
  // --- Text ---
  {
    name: 'UPPER',
    label: 'Uppercase',
    category: 'text',
    description: 'Convert text to uppercase',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  {
    name: 'LOWER',
    label: 'Lowercase',
    category: 'text',
    description: 'Convert text to lowercase',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  {
    name: 'TRIM',
    label: 'Trim whitespace',
    category: 'text',
    description: 'Remove leading and trailing whitespace',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  {
    name: 'CONCAT',
    label: 'Concatenate',
    category: 'text',
    description: 'Join multiple values together',
    isUnary: false,
    isMultiInput: true,
    defaultConfig: {
      parts: [
        { kind: 'literal', value: '' },
        { kind: 'literal', value: '' },
      ],
    },
  },
  {
    name: 'SPLIT',
    label: 'Split text',
    category: 'text',
    description: 'Split text by delimiter and get an element',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { delimiter: ',', index: 0 },
  },
  {
    name: 'REPLACE',
    label: 'Find & Replace',
    category: 'text',
    description: 'Replace occurrences of a substring',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { find: '', replaceWith: '' },
  },
  {
    name: 'TO_CHAR',
    label: 'To text',
    category: 'text',
    description: 'Convert value to text/string representation',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { format: '' },
  },
  {
    name: 'SUBSTRING',
    label: 'Substring',
    category: 'text',
    description: 'Extract part of text',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { start: 1, length: 10 },
  },
  {
    name: 'LEFT',
    label: 'Left chars',
    category: 'text',
    description: 'Get first N characters',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { length: 5 },
  },
  {
    name: 'RIGHT',
    label: 'Right chars',
    category: 'text',
    description: 'Get last N characters',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { length: 5 },
  },
  {
    name: 'LPAD',
    label: 'Pad left',
    category: 'text',
    description: 'Pad text on the left to a fixed length',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { padLength: 10, padChar: '0' },
  },
  {
    name: 'RPAD',
    label: 'Pad right',
    category: 'text',
    description: 'Pad text on the right to a fixed length',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { padLength: 10, padChar: ' ' },
  },
  {
    name: 'LENGTH',
    label: 'Text length',
    category: 'text',
    description: 'Get the length of the text',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  // --- Logic ---
  {
    name: 'COALESCE',
    label: 'First non-empty',
    category: 'logic',
    description: 'Return first non-null value from a list',
    isUnary: false,
    isMultiInput: true,
    defaultConfig: {
      fallbacks: [
        { kind: 'literal', value: '' },
        { kind: 'literal', value: '' },
      ],
    },
  },
  {
    name: 'IF',
    label: 'If / Then / Else',
    category: 'logic',
    description: 'Conditional: if condition is true, use one value, otherwise another',
    isUnary: false,
    isMultiInput: true,
    defaultConfig: {
      conditionField: { kind: 'literal', value: '' },
      operator: '=',
      conditionValue: '',
      trueValue: { kind: 'literal', value: '' },
      falseValue: { kind: 'literal', value: '' },
    },
  },
  {
    name: 'CAST',
    label: 'Convert type',
    category: 'logic',
    description: 'Convert to a different data type',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { targetType: 'INT' },
  },
  {
    name: 'IS_NULL',
    label: 'Is empty?',
    category: 'logic',
    description: 'Check if value is empty/null (returns true/false)',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  {
    name: 'IS_NOT_NULL',
    label: 'Is not empty?',
    category: 'logic',
    description: 'Check if value is not empty/null (returns true/false)',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
  {
    name: 'NVL',
    label: 'Default value',
    category: 'logic',
    description: 'Use a default if value is empty',
    isUnary: false,
    isMultiInput: false,
    defaultConfig: { fallbacks: [{ kind: 'literal', value: '' }] },
  },
  {
    name: 'LITERAL',
    label: 'Constant value',
    category: 'logic',
    description: 'Use a fixed constant value (ignores input)',
    isUnary: false,
    isMultiInput: false,
    defaultConfig: { literalValue: '' },
  },
  {
    name: 'CASE_WHEN',
    label: 'Case / When',
    category: 'logic',
    description: 'Map values: when X then Y, when A then B, else C',
    isUnary: false,
    isMultiInput: false,
    defaultConfig: { branches: [{ condition: '', result: '' }], elseResult: '' },
  },
  // --- Date ---
  {
    name: 'DATE_FORMAT',
    label: 'Format date',
    category: 'date',
    description: 'Format a date to a specific string pattern',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { format: '%Y-%m-%d' },
  },
  {
    name: 'PARSE_DATE',
    label: 'Parse date',
    category: 'date',
    description: 'Parse a string into a date value',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { format: '%Y-%m-%d' },
  },
  {
    name: 'TO_DATE',
    label: 'Convert to date',
    category: 'date',
    description: 'Convert text to a date value',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { format: '%Y-%m-%d' },
  },
  {
    name: 'TO_TIMESTAMP',
    label: 'Convert to timestamp',
    category: 'date',
    description: 'Convert text to a timestamp value',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { format: '%Y-%m-%d %H:%M:%S' },
  },
  // --- Numeric ---
  {
    name: 'ROUND',
    label: 'Round',
    category: 'numeric',
    description: 'Round to specified decimal places',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: { decimals: 2 },
  },
  {
    name: 'ABS',
    label: 'Absolute value',
    category: 'numeric',
    description: 'Get the absolute (positive) value',
    isUnary: true,
    isMultiInput: false,
    defaultConfig: {},
  },
];

export const FUNCTION_REGISTRY_MAP: Record<string, FunctionRegistryEntry> = Object.fromEntries(
  FUNCTION_REGISTRY.map((entry) => [entry.name, entry])
);

export const FUNCTION_CATEGORIES: { key: FunctionCategory; label: string }[] = [
  { key: 'text', label: 'Text' },
  { key: 'logic', label: 'Logic' },
  { key: 'date', label: 'Date' },
  { key: 'numeric', label: 'Numeric' },
];

export const getFunctionsByCategory = (category: FunctionCategory): FunctionRegistryEntry[] =>
  FUNCTION_REGISTRY.filter((f) => f.category === category);

export const DATE_FORMAT_PRESETS = [
  { label: 'YYYY-MM-DD', value: '%Y-%m-%d' },
  { label: 'MM/DD/YYYY', value: '%m/%d/%Y' },
  { label: 'DD/MM/YYYY', value: '%d/%m/%Y' },
  { label: 'YYYY-MM-DD HH:MM:SS', value: '%Y-%m-%d %H:%M:%S' },
  { label: 'Month DD, YYYY', value: '%B %d, %Y' },
];

export const CAST_TYPE_OPTIONS = [
  { label: 'Integer', value: 'INT' },
  { label: 'Float', value: 'FLOAT' },
  { label: 'String', value: 'STRING' },
  { label: 'Date', value: 'DATE' },
  { label: 'Boolean', value: 'BOOLEAN' },
];

export const IF_OPERATOR_OPTIONS = [
  { label: 'equals', value: '=' },
  { label: 'not equals', value: '!=' },
  { label: 'greater than', value: '>' },
  { label: 'less than', value: '<' },
  { label: 'contains', value: 'LIKE' },
  { label: 'is empty', value: 'IS NULL' },
  { label: 'is not empty', value: 'IS NOT NULL' },
];
